/**
 * Shared software-crop targets for the local camera and screenshare.
 *
 * This is the web counterpart of the native {@code CameraZoomState}: the zoom
 * controller writes the crop that is still needed after hardware zoom
 * (requested / applied), and whichever stream effect is currently drawing that
 * track's frames polls it once per frame. The virtual background effect and
 * the standalone {@link CameraZoomEffect} therefore apply the same framing
 * without either of them knowing which one is installed.
 */

/**
 * Anything closer to 1x than this is treated as no crop.
 */
export const CROP_EPSILON = 0.01;

const softwareZoom = {
    camera: 1,
    desktop: 1
};

/**
 * Stores the remaining crop for a local video type.
 *
 * @param {string} videoType - 'camera' or 'desktop'.
 * @param {number} zoom - Crop factor, 1 for none.
 * @returns {void}
 */
export function setSoftwareZoom(videoType, zoom) {
    softwareZoom[videoType] = Number.isFinite(zoom) && zoom > 1 ? zoom : 1;
}

/**
 * Returns the remaining crop for a local video type.
 *
 * @param {string} videoType - 'camera' or 'desktop'.
 * @returns {number}
 */
export function getSoftwareZoom(videoType) {
    return softwareZoom[videoType] || 1;
}

/**
 * Source rectangle of a centered crop that a drawImage call scales back to the
 * full frame size, i.e. the same operation the native capture path performs.
 *
 * @param {number} width - Frame width.
 * @param {number} height - Frame height.
 * @param {number} zoom - Crop factor.
 * @returns {{ sx: number, sy: number, sw: number, sh: number }}
 */
export function getCropRect(width, height, zoom) {
    if (!(zoom > 1 + CROP_EPSILON) || width < 2 || height < 2) {
        return {
            sx: 0,
            sy: 0,
            sw: width,
            sh: height
        };
    }

    const sw = Math.min(width, Math.max(2, Math.round(width / zoom)));
    const sh = Math.min(height, Math.max(2, Math.round(height / zoom)));

    return {
        sx: Math.round((width - sw) / 2),
        sy: Math.round((height - sh) / 2),
        sw,
        sh
    };
}
